
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { PlusCircle, Save, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AwardMembership, award_organization } from '@/types/awardMembership';
import { supabase } from '@/integrations/supabase/client';

// Schema for form validation
const formSchema = z.object({
  organization: z.enum(['Grammy', 'Billboard', 'BET', 'MTV', 'ASCAP', 'BMI', 'Other'] as const),
  years_of_membership: z.coerce.number().min(0).optional(),
  voting_categories: z.array(z.string()),
  allow_campaign_contact: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

interface AwardMembershipFormProps {
  membership?: AwardMembership;
  onSuccess?: () => void;
}

const AwardMembershipForm: React.FC<AwardMembershipFormProps> = ({ membership, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [categoryInput, setCategoryInput] = React.useState('');
  
  // Initialize the form with default values or existing membership data
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: membership ? {
      organization: membership.organization,
      years_of_membership: membership.years_of_membership,
      voting_categories: membership.voting_categories || [],
      allow_campaign_contact: membership.allow_campaign_contact,
    } : {
      organization: 'Grammy' as award_organization,
      years_of_membership: undefined,
      voting_categories: [],
      allow_campaign_contact: false,
    },
  });

  const addCategory = () => {
    if (!categoryInput.trim()) return;
    
    const currentCategories = form.getValues('voting_categories') || [];
    if (!currentCategories.includes(categoryInput.trim())) {
      form.setValue('voting_categories', [...currentCategories, categoryInput.trim()]);
      setCategoryInput('');
    }
  };

  const removeCategory = (category: string) => {
    const currentCategories = form.getValues('voting_categories');
    form.setValue(
      'voting_categories', 
      currentCategories.filter(c => c !== category)
    );
  };

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      if (membership) {
        // Update existing membership
        const { error } = await supabase
          .from('award_memberships')
          .update({
            organization: values.organization,
            years_of_membership: values.years_of_membership,
            voting_categories: values.voting_categories,
            allow_campaign_contact: values.allow_campaign_contact,
            updated_at: new Date().toISOString(),
          })
          .eq('id', membership.id);
          
        if (error) throw error;
        toast.success('Award membership updated successfully');
      } else {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          toast.error('You must be logged in to add an award membership');
          setIsSubmitting(false);
          return;
        }
        
        // Insert new membership with user_id
        const { error } = await supabase
          .from('award_memberships')
          .insert({
            user_id: user.id,
            organization: values.organization,
            years_of_membership: values.years_of_membership,
            voting_categories: values.voting_categories,
            allow_campaign_contact: values.allow_campaign_contact,
          });
          
        if (error) throw error;
        toast.success('Award membership added successfully');
      }
      
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error saving award membership:', error);
      toast.error('Failed to save award membership');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{membership ? 'Edit Award Membership' : 'Add Award Membership'}</CardTitle>
        <CardDescription>
          {membership 
            ? 'Update your award membership details' 
            : 'Add details about your award voting membership'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="organization"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Award Organization</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an organization" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Grammy">Grammy Awards</SelectItem>
                      <SelectItem value="Billboard">Billboard Music Awards</SelectItem>
                      <SelectItem value="BET">BET Awards</SelectItem>
                      <SelectItem value="MTV">MTV Awards</SelectItem>
                      <SelectItem value="ASCAP">ASCAP Awards</SelectItem>
                      <SelectItem value="BMI">BMI Awards</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="years_of_membership"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Years of Membership</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      placeholder="How many years have you been a member?"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="space-y-2">
              <FormLabel>Voting Categories</FormLabel>
              <div className="flex items-center gap-2">
                <Input
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  placeholder="Add a voting category"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addCategory}
                >
                  <PlusCircle className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2 mt-2">
                {form.getValues('voting_categories')?.map((category) => (
                  <Badge 
                    key={category} 
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {category}
                    <XCircle 
                      className="h-3 w-3 ml-1 cursor-pointer" 
                      onClick={() => removeCategory(category)}
                    />
                  </Badge>
                ))}
              </div>
              {form.formState.errors.voting_categories && (
                <p className="text-sm text-destructive">{form.formState.errors.voting_categories.message}</p>
              )}
            </div>
            
            <FormField
              control={form.control}
              name="allow_campaign_contact"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Allow Campaign Contact
                    </FormLabel>
                    <FormDescription>
                      Allow artists to discover and contact you regarding award campaigns
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <CardFooter className="flex justify-end pt-4 px-0">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {isSubmitting ? 'Saving...' : 'Save Membership'}
              </Button>
            </CardFooter>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default AwardMembershipForm;
